import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useAutonomousAgent } from '../contexts/AutonomousAgentContext'
import { StatusBadge } from '../components/ui/StatusBadge'
import ProgressBar from '../components/ui/ProgressBar'
import FileUpload from '../components/files/FileUpload'
import FileList from '../components/files/FileList'
import Comments from '../components/files/Comments'
import ActivityLog from '../components/activity/ActivityLog'
import AIReportButton from '../components/ai/AIReportButton'
import AgentStatusPanel from '../components/ai/AgentStatusPanel'
import TacticalPlanView from '../components/tactical/TacticalPlanView'
import ExecutionView from '../components/execution/ExecutionView'
import Modal from '../components/ui/Modal'
import { ArrowLeft, ChevronRight, Plus, Building2, FileText, MessageSquare, Activity, BarChart3, Pencil, Trash2, Settings, Download, LayoutList, Calendar } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts'
import TaskDashboard from '../components/tasks/TaskDashboard'
import FichaProyecto from '../components/etapa0/FichaProyecto'

const PROJECT_STAGE_ICONS = ['🌍', '🏭', '🎯']

export default function ConsultancyPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const { initQueue } = useAutonomousAgent()
  const [consultancy, setConsultancy]   = useState(null)
  const [projectPhases, setProjectPhases] = useState([])
  const [bus, setBus]                   = useState([])
  const [loading, setLoading]           = useState(true)
  const [activeTab, setActiveTab]       = useState('units')
  const [projectPhase, setProjectPhase] = useState('strategic') // 'strategic' | 'tactical' | 'execution'

  // Create BU modal
  const [createOpen, setCreateOpen]     = useState(false)
  const [buForm, setBuForm]             = useState({ name: '', description: '' })
  const [saving, setSaving]             = useState(false)

  // Edit BU modal
  const [editBuOpen, setEditBuOpen]     = useState(false)
  const [editBuTarget, setEditBuTarget] = useState(null)
  const [editBuForm, setEditBuForm]     = useState({ name: '', description: '' })
  const [savingBu, setSavingBu]         = useState(false)

  // Edit Phase modal
  const [editPhaseOpen, setEditPhaseOpen]     = useState(false)
  const [editPhaseTarget, setEditPhaseTarget] = useState(null)
  const [editPhaseForm, setEditPhaseForm]     = useState({ name: '', description: '' })
  const [savingPhase, setSavingPhase]         = useState(false)

  // Edit Consultancy modal
  const [editConsOpen, setEditConsOpen]   = useState(false)
  const [editConsForm, setEditConsForm]   = useState({ name: '', client_company: '', industry: '', description: '', status: 'active' })
  const [savingCons, setSavingCons]       = useState(false)

  // All tasks (for the Tareas tab)
  const [allTasks, setAllTasks]           = useState([])

  // Gantt
  const [ficha, setFicha]                 = useState(null)
  const [ganttPhases, setGanttPhases]     = useState([])

  useEffect(() => { fetchData() }, [id])

  const TASKS_SELECT = 'id, name, status, description, priority, assignee_id'
  const SECTIONS_SELECT = `id, name, status, assignee_id, tasks(${TASKS_SELECT})`

  async function fetchData() {
    setLoading(true)
    const [{ data: cons }, { data: buList }, { data: projPhases }, { data: fichaData }] = await Promise.all([
      supabase.from('consultancies').select('*').eq('id', id).single(),
      supabase.from('business_units')
        .select('*, phases(id, progress, status)')
        .eq('consultancy_id', id)
        .order('bu_order'),
      supabase.from('phases')
        .select('*, sections(id, status)')
        .eq('consultancy_id', id)
        .is('business_unit_id', null)
        .order('phase_order'),
      supabase.from('project_ficha')
        .select('fecha_inicio, fecha_diagnostico, fecha_entrega_plan, fecha_cierre')
        .eq('consultancy_id', id)
        .maybeSingle(),
    ])
    setConsultancy(cons)
    setBus(buList ?? [])
    setProjectPhases(projPhases ?? [])
    setFicha(fichaData)
    // Gantt: project phases enriched with dates
    setGanttPhases((projPhases ?? []).filter(p => p.phase_order !== undefined))
    setLoading(false)

    // Trigger autonomous agent queue for this project
    if (cons) {
      const projectData = { consultancy: cons, phases: projPhases ?? [] }
      initQueue(id, cons.name, projectData)
    }

    // Load all tasks with assignee info for the Tareas dashboard
    const [{ data: taskPhases }, { data: profs }] = await Promise.all([
      supabase.from('phases').select(`id, name, sections(${SECTIONS_SELECT})`).eq('consultancy_id', id),
      supabase.from('profiles').select('id, full_name'),
    ])
    const profileMap = Object.fromEntries((profs ?? []).map(p => [p.id, p]))
    const flat = []
    for (const phase of taskPhases ?? []) {
      for (const section of phase.sections ?? []) {
        for (const task of section.tasks ?? []) {
          flat.push({
            ...task,
            assignee: task.assignee_id ? (profileMap[task.assignee_id] ?? null) : null,
            phaseName: phase.name,
            sectionName: section.name,
            sectionAssignee: section.assignee_id ? (profileMap[section.assignee_id] ?? null) : null,
          })
        }
      }
    }
    setAllTasks(flat)
  }

  // ── Plantilla estándar NVC (basada en Harinas Industriales — Ersa) ──
  // Estructura fija: 6 fases + secciones. No depende de datos en BD.
  const BU_TEMPLATE = [
    {
      name: 'Análisis del Mercado',
      description: 'Tamaño, dinámica, segmentos y rentabilidad del mercado objetivo',
      phase_order: 1,
      sections: [
        { name: 'Desempeño de mercado',       description: null,                                                                                                                                      section_order: 1 },
        { name: 'Soluciones (Producto)',       description: 'Conjunto de productos y/o servicios que una organización desarrolla y ofrece al mercado para resolver necesidades específicas de los consumidores', section_order: 2 },
        { name: 'Canales',                     description: 'Conjunto estructurado de medios, plataformas y puntos de contacto utilizados para la distribución de valor/soluciones/productos.',        section_order: 3 },
        { name: 'Participaciones de mercado', description: 'Identificar participación de mercado de los competidores relevantes en este segmento',                                                     section_order: 4 },
        { name: 'Precio',                     description: 'Esta sección es el resultado del precio del mercado y de los competidores',                                                               section_order: 5 },
        { name: 'Comunicación',               description: 'Mix de medios de comunicación',                                                                                                          section_order: 6 },
      ],
    },
    {
      name: 'Análisis del Consumidor',
      description: 'Segmentación, perfiles, necesidades y comportamiento del consumidor',
      phase_order: 2,
      // Solo "Segmentación" como sección estática. Las 4 sub-secciones por
      // perfil (Necesidades y Tensiones, Sobre la Solución, Adopción de la
      // Categoría, Adopción de Marca y Nivel de Lealtad) se crean
      // dinámicamente cuando el usuario añade un perfil de consumidor en
      // PhasePage.createConsumerProfile.
      sections: [
        { name: 'Segmentación', description: 'Identificar los segmentos de consumidores relevantes del mercado', section_order: 1 },
      ],
    },
    {
      name: 'Análisis Competitivo',
      description: 'Fichas de competidores, posicionamiento y espacios no ocupados',
      phase_order: 3,
      sections: [], // Los competidores se agregan dinámicamente desde PhasePage
    },
    {
      name: 'Análisis Interno',
      description: 'Capacidades organizacionales, ventajas competitivas y brechas',
      phase_order: 4,
      sections: [
        { name: 'Evaluar el cumplimiento de los objetivos y pilares estratégicos', description: null,                                                                                                                               section_order: 1 },
        { name: 'Estrategia vista desde el P&L',  description: 'La parte inicial del análisis interno es entender la estrategia mediante el P&L del negocio',                                        section_order: 2 },
        { name: 'Posicionamiento',                description: null,                                                                                                                                  section_order: 3 },
        { name: 'Soluciones (Producto)',           description: 'Conjunto de productos y/o servicios que una organización desarrolla y ofrece al mercado para resolver necesidades específicas de los consumidores', section_order: 4 },
        { name: 'Precio',                         description: '¿Qué estrategia de precio tienen los competidores? Postura de precio',                                                               section_order: 5 },
        { name: 'Canales',                        description: 'Conjunto estructurado de medios, plataformas y puntos de contacto utilizados para la distribución de valor/soluciones/productos.',    section_order: 6 },
        { name: 'Capacidad Industrial',           description: null,                                                                                                                                  section_order: 7 },
        { name: 'Estrategia de comunicación',     description: 'Estrategia de comunicación y de marca para cada competidor',                                                                         section_order: 8 },
      ],
    },
    {
      name: 'Definición de Estrategia por Mercado',
      description: 'Posicionamiento, portafolio, precio, canales y comunicación por mercado',
      phase_order: 5,
      sections: [
        { name: 'Posicionamiento',   description: null, section_order: 1 },
        { name: 'Portafolio',        description: null, section_order: 2 },
        { name: 'Precio',            description: null, section_order: 3 },
        { name: 'Canales',           description: null, section_order: 4 },
        { name: 'Comunicación',      description: null, section_order: 5 },
        { name: 'Objetivos y Pilares', description: null, section_order: 6 },
        { name: 'Escenarios',        description: null, section_order: 7 },
      ],
    },
    {
      name: 'Consolidación en Estrategia Corporativa',
      description: 'Síntesis de todas las unidades en una estrategia corporativa integrada',
      phase_order: 6,
      sections: [
        { name: 'Asignación de Recursos',  description: null, section_order: 1 },
        { name: 'Sinergias entre Unidades', description: null, section_order: 2 },
        { name: 'Narrativa Corporativa',   description: null, section_order: 3 },
        { name: 'Mapa de Stakeholders',    description: null, section_order: 4 },
        { name: 'Cuadro de Mando',         description: null, section_order: 5 },
      ],
    },
  ]

  // Aplica la plantilla estándar NVC a una UdN recién creada
  async function applyBUTemplate(newBuId) {
    // Borrar siempre las fases previas (trigger o código viejo) antes de aplicar plantilla
    await supabase.from('phases').delete().eq('business_unit_id', newBuId)

    for (const phase of BU_TEMPLATE) {
      const { data: newPhase } = await supabase
        .from('phases')
        .insert({
          business_unit_id: newBuId,
          consultancy_id:   id,
          name:             phase.name,
          description:      phase.description || null,
          phase_order:      phase.phase_order,
          status:           'pending',
          progress:         0,
        })
        .select()
        .single()

      if (!newPhase || !phase.sections.length) continue

      await supabase.from('sections').insert(
        phase.sections.map(s => ({
          phase_id:      newPhase.id,
          name:          s.name,
          description:   s.description || null,
          section_order: s.section_order,
          status:        'pending',
        }))
      )
    }
  }

  async function createBU(e) {
    e.preventDefault()
    setSaving(true)
    const nextOrder = bus.length
    const { data: newBu, error } = await supabase
      .from('business_units')
      .insert({ ...buForm, consultancy_id: id, bu_order: nextOrder })
      .select()
      .single()
    if (!error && newBu) {
      // Aplicar plantilla estándar automáticamente
      await applyBUTemplate(newBu.id)
      setCreateOpen(false)
      setBuForm({ name: '', description: '' })
      fetchData()
    }
    setSaving(false)
  }

  function openEditBu(e, bu) {
    e.stopPropagation()
    setEditBuTarget(bu)
    setEditBuForm({ name: bu.name, description: bu.description ?? '' })
    setEditBuOpen(true)
  }

  async function saveEditBu(e) {
    e.preventDefault()
    setSavingBu(true)
    await supabase.from('business_units').update(editBuForm).eq('id', editBuTarget.id)
    setBus(prev => prev.map(b => b.id === editBuTarget.id ? { ...b, ...editBuForm } : b))
    setEditBuOpen(false)
    setSavingBu(false)
  }

  async function deleteBu(e, buId) {
    e.stopPropagation()
    if (!confirm('¿Eliminar esta unidad de negocio y todas sus etapas? Esta acción no se puede deshacer.')) return
    await supabase.from('business_units').delete().eq('id', buId)
    setBus(prev => prev.filter(b => b.id !== buId))
  }

  function openEditPhase(e, phase) {
    e.stopPropagation()
    setEditPhaseTarget(phase)
    setEditPhaseForm({ name: phase.name, description: phase.description ?? '' })
    setEditPhaseOpen(true)
  }

  async function saveEditPhase(e) {
    e.preventDefault()
    setSavingPhase(true)
    await supabase.from('phases').update(editPhaseForm).eq('id', editPhaseTarget.id)
    setProjectPhases(prev => prev.map(p => p.id === editPhaseTarget.id ? { ...p, ...editPhaseForm } : p))
    setEditPhaseOpen(false)
    setSavingPhase(false)
  }

  async function deletePhase(e, phaseId) {
    e.stopPropagation()
    if (!confirm('¿Eliminar esta etapa y todas sus secciones? Esta acción no se puede deshacer.')) return
    await supabase.from('phases').delete().eq('id', phaseId)
    setProjectPhases(prev => prev.filter(p => p.id !== phaseId))
  }

  function openEditCons() {
    setEditConsForm({
      name: consultancy.name,
      client_company: consultancy.client_company,
      industry: consultancy.industry ?? '',
      description: consultancy.description ?? '',
      status: consultancy.status ?? 'active',
    })
    setEditConsOpen(true)
  }

  async function saveEditCons(e) {
    e.preventDefault()
    setSavingCons(true)
    await supabase.from('consultancies').update(editConsForm).eq('id', id)
    setConsultancy(c => ({ ...c, ...editConsForm }))
    setEditConsOpen(false)
    setSavingCons(false)
  }

  async function deleteConsultancy() {
    if (!confirm(`¿Eliminar la consultoría "${consultancy.name}" y todos sus datos? Esta acción no se puede deshacer.`)) return
    await supabase.from('consultancies').delete().eq('id', id)
    navigate('/')
  }

  async function generateStatusPDF() {
    const { default: jsPDF } = await import('jspdf')
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const W = 210, margin = 16, contentW = W - margin * 2
    let y = 0

    // ── Header bar ──
    doc.setFillColor(30, 58, 95)
    doc.rect(0, 0, W, 28, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('NEXT VALLEY CONSULTANTS', margin, 11)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text('Informe de Estado de Consultoría', margin, 18)
    doc.text(new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' }), W - margin, 18, { align: 'right' })
    y = 36

    // ── Consultancy info ──
    doc.setTextColor(30, 58, 95)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(consultancy.name, margin, y); y += 7
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(`Empresa: ${consultancy.client_company}`, margin, y); y += 5
    if (consultancy.industry) { doc.text(`Industria: ${consultancy.industry}`, margin, y); y += 5 }
    const statusMap = { active: 'Activa', paused: 'Pausada', completed: 'Completada' }
    doc.text(`Estado: ${statusMap[consultancy.status] ?? consultancy.status}`, margin, y); y += 5
    if (consultancy.start_date || consultancy.end_date) {
      const sd = consultancy.start_date ? new Date(consultancy.start_date + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
      const ed = consultancy.end_date   ? new Date(consultancy.end_date   + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
      doc.text(`Período: ${sd} → ${ed}`, margin, y); y += 5
    }
    y += 4

    // Divider
    doc.setDrawColor(220, 220, 220)
    doc.line(margin, y, W - margin, y); y += 6

    // ── Fetch all phases with sections & tasks ──
    const { data: allPhases } = await supabase
      .from('phases')
      .select('*, sections(*, tasks(*))')
      .eq('consultancy_id', id)
      .order('phase_order')

    const STATUS_ES = { pending: 'Pendiente', in_progress: 'En progreso', review: 'En revisión', completed: 'Completado', blocked: 'Bloqueado' }

    // ── Grand totals ──
    let grandTotal = 0, grandDone = 0
    ;(allPhases ?? []).forEach(ph => (ph.sections ?? []).forEach(sec => {
      grandTotal += sec.tasks?.length ?? 0
      grandDone  += sec.tasks?.filter(t => t.status === 'completed').length ?? 0
    }))
    const globalPct = grandTotal > 0 ? Math.round((grandDone / grandTotal) * 100) : 0

    doc.setFillColor(245, 247, 250)
    doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F')
    doc.setTextColor(30, 58, 95)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text(`Progreso global: ${grandDone}/${grandTotal} tareas completadas (${globalPct}%)`, margin + 4, y + 9)
    y += 20

    // ── Phases ──
    for (const ph of (allPhases ?? [])) {
      const phTotal = (ph.sections ?? []).reduce((s, sec) => s + (sec.tasks?.length ?? 0), 0)
      const phDone  = (ph.sections ?? []).reduce((s, sec) => s + (sec.tasks?.filter(t => t.status === 'completed').length ?? 0), 0)
      const phPct   = phTotal > 0 ? Math.round((phDone / phTotal) * 100) : 0

      // Page break check
      if (y > 260) { doc.addPage(); y = 16 }

      doc.setFillColor(30, 58, 95)
      doc.roundedRect(margin, y, contentW, 9, 2, 2, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.text(ph.name, margin + 3, y + 6)
      doc.text(`${phDone}/${phTotal} (${phPct}%)`, W - margin - 3, y + 6, { align: 'right' })
      y += 12

      for (const sec of (ph.sections ?? [])) {
        const secTotal = sec.tasks?.length ?? 0
        const secDone  = sec.tasks?.filter(t => t.status === 'completed').length ?? 0
        const secPct   = secTotal > 0 ? Math.round((secDone / secTotal) * 100) : 0

        if (y > 270) { doc.addPage(); y = 16 }

        doc.setTextColor(60, 60, 60)
        doc.setFontSize(8.5)
        doc.setFont('helvetica', 'bold')
        doc.text(`  ${sec.name}`, margin + 2, y)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(120, 120, 120)
        doc.text(`${secDone}/${secTotal} tareas · ${STATUS_ES[sec.status] ?? sec.status}`, W - margin - 3, y, { align: 'right' })
        y += 5

        // Task list (up to 10 per section)
        for (const task of (sec.tasks ?? []).slice(0, 10)) {
          if (y > 275) { doc.addPage(); y = 16 }
          const dot = task.status === 'completed' ? '✓' : '○'
          doc.setTextColor(task.status === 'completed' ? 34 : 150, task.status === 'completed' ? 197 : 150, task.status === 'completed' ? 94 : 150)
          doc.setFontSize(7.5)
          doc.text(`    ${dot} ${task.name}`, margin + 4, y)
          doc.setTextColor(170, 170, 170)
          doc.text(STATUS_ES[task.status] ?? task.status, W - margin - 3, y, { align: 'right' })
          y += 4.5
        }
        if ((sec.tasks?.length ?? 0) > 10) {
          doc.setTextColor(170, 170, 170)
          doc.setFontSize(7)
          doc.text(`    ... y ${sec.tasks.length - 10} tareas más`, margin + 4, y)
          y += 4.5
        }
        y += 2
      }
      y += 3
    }

    // ── Footer ──
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(7)
      doc.setTextColor(180, 180, 180)
      doc.text(`Página ${i} de ${pageCount}  ·  Next Valley Consultants  ·  Generado ${new Date().toLocaleDateString('es')}`, W / 2, 292, { align: 'center' })
    }

    const safeName = consultancy.name.replace(/[^a-z0-9]/gi, '_')
    doc.save(`Informe_${safeName}.pdf`)
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )
  if (!consultancy) return <div className="text-center py-20 text-gray-400">Consultoría no encontrada</div>

  const phasesBefore = projectPhases.filter(p => p.phase_order <= 3)
  const phasesAfter  = projectPhases.filter(p => p.phase_order > 3)

  const allPhaseProgresses = [
    ...projectPhases.map(p => p.progress ?? 0),
    ...bus.flatMap(bu => (bu.phases ?? []).map(p => p.progress ?? 0)),
  ]
  const overallProgress = allPhaseProgresses.length
    ? Math.round(allPhaseProgresses.reduce((s, n) => s + n, 0) / allPhaseProgresses.length)
    : 0

  const tabs = [
    { id: 'units',    label: 'Unidades de Negocio', icon: Building2 },
    { id: 'gantt',    label: 'Cronograma',          icon: Calendar  },
    { id: 'tareas',   label: 'Tareas',              icon: LayoutList },
    { id: 'files',    label: 'Archivos',            icon: FileText  },
    { id: 'comments', label: 'Comentarios',         icon: MessageSquare },
    { id: 'activity', label: 'Actividad',           icon: Activity },
  ]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand-600 transition-colors">
        <ArrowLeft size={16} /> Dashboard
      </button>

      {/* Header card */}
      <div className="card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-brand-700 flex items-center justify-center text-white font-bold text-xl md:text-2xl flex-shrink-0">
            {consultancy.client_company.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            {/* Top row: title + admin icon buttons */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="text-base md:text-xl font-bold text-gray-900 leading-tight">{consultancy.name}</h1>
                  <StatusBadge status={consultancy.status} />
                </div>
                <p className="text-gray-500 text-sm mb-0.5">{consultancy.client_company}</p>
                {consultancy.industry && <p className="text-xs text-gray-400">{consultancy.industry}</p>}
                {consultancy.description && <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{consultancy.description}</p>}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button onClick={openEditCons}
                    className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors" title="Editar consultoría">
                    <Pencil size={15} />
                  </button>
                  <button onClick={deleteConsultancy}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar consultoría">
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>
            {/* Action buttons — full width row on mobile */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <AIReportButton consultancyId={id} reportType="strategic_plan" label="Plan Estratégico" />
              <button
                onClick={generateStatusPDF}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors border border-brand-200"
                title="Descargar informe de estado en PDF"
              >
                <Download size={13} /> Informe PDF
              </button>
              <button
                onClick={() => navigate(`/consultancy/${id}/dashboard`)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shadow-sm"
              >
                <BarChart3 size={15} /> Cuadro de Mando
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progreso global</span><span>{overallProgress}%</span>
          </div>
          <ProgressBar value={overallProgress} size="md" showLabel={false} />
        </div>
      </div>

      {/* ── 3-Phase Project Navigation ── */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-2xl">
        {[
          { id: 'strategic', emoji: '📋', label: 'Estratégico', labelFull: 'Plan Estratégico', desc: 'Análisis y estrategia NVC' },
          { id: 'tactical',  emoji: '⚡', label: 'Táctico',     labelFull: 'Plan Táctico',     desc: 'Objetivos, pilares y actividades' },
          { id: 'execution', emoji: '🎯', label: 'Ejecución',   labelFull: 'Ejecución',        desc: 'Seguimiento, KPIs y OKRs' },
        ].map(ph => (
          <button key={ph.id} onClick={() => setProjectPhase(ph.id)}
            className={`flex-1 rounded-xl py-2 px-1 md:px-3 text-center transition-all ${
              projectPhase === ph.id
                ? 'bg-white shadow-sm border border-gray-200'
                : 'hover:bg-white/50'
            }`}>
            <p className={`text-xs md:text-sm font-semibold ${projectPhase === ph.id ? 'text-brand-700' : 'text-gray-500'}`}>
              <span className="md:hidden">{ph.emoji} {ph.label}</span>
              <span className="hidden md:inline">{ph.emoji} {ph.labelFull}</span>
            </p>
            <p className="text-xs text-gray-400 hidden md:block">{ph.desc}</p>
          </button>
        ))}
      </div>

      {/* ── STRATEGIC PLAN (existing content) ── */}
      {projectPhase === 'strategic' && (
        <>
          {/* ── Agent Status Panel (temporarily hidden) ── */}
          {/* <AgentStatusPanel consultancyId={id} phases={projectPhases} /> */}

          {/* ── Ficha del Proyecto ── */}
          <FichaProyecto consultancyId={id} />

          {/* ── Project-level Phases BEFORE BUs (1-3) ── */}
      {phasesBefore.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-brand-500" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Etapas del Proyecto</h2>
          </div>
          {phasesBefore.map((phase, idx) => {
            const sections = phase.sections ?? []
            const completed   = sections.filter(s => s.status === 'completed').length
            const review      = sections.filter(s => s.status === 'review').length
            const inProgress  = sections.filter(s => s.status === 'in_progress').length
            const blocked     = sections.filter(s => s.status === 'blocked').length
            const pending     = sections.filter(s => !s.status || s.status === 'pending').length
            const totalSecs   = sections.length
            const widthOf     = n => totalSecs > 0 ? `${(n / totalSecs) * 100}%` : '0%'
            return (
              <div key={phase.id} className="w-full card p-4 hover:shadow-md hover:border-brand-300 transition-all group">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/consultancy/${id}/phase/${phase.id}`)}
                    className="flex items-center gap-3 flex-1 text-left min-w-0"
                  >
                    <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center text-lg flex-shrink-0">
                      {PROJECT_STAGE_ICONS[idx] ?? '📋'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-900 group-hover:text-brand-700 transition-colors">
                          {phase.name}
                        </span>
                        <StatusBadge status={phase.status} />
                      </div>
                      {phase.phase_order !== 0 && (
                        <>
                          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                            {completed  > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{completed} completada{completed !== 1 ? 's' : ''}</span>}
                            {review     > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />{review} en revisión</span>}
                            {inProgress > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />{inProgress} en progreso</span>}
                            {blocked    > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />{blocked} bloqueada{blocked !== 1 ? 's' : ''}</span>}
                            {pending    > 0 && totalSecs > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-gray-400" />{pending} por empezar</span>}
                            {totalSecs === 0 && <span className="text-[11px] text-gray-400 italic">Sin secciones</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 max-w-xs h-2 bg-gray-100 rounded-full overflow-hidden flex">
                              <div className="bg-emerald-500 h-full transition-all" style={{ width: widthOf(completed) }} />
                              <div className="bg-violet-500 h-full transition-all"  style={{ width: widthOf(review) }} />
                              <div className="bg-blue-500 h-full transition-all"    style={{ width: widthOf(inProgress) }} />
                              <div className="bg-red-400 h-full transition-all"     style={{ width: widthOf(blocked) }} />
                            </div>
                            <span className="text-xs text-gray-500 tabular-nums">{phase.progress ?? 0}%</span>
                          </div>
                        </>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-brand-400 flex-shrink-0" />
                  </button>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0 ml-1">
                      <button onClick={e => openEditPhase(e, phase)}
                        className="p-2 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition-colors" title="Editar etapa">
                        <Pencil size={14} />
                      </button>
                      <button onClick={e => deletePhase(e, phase.id)}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar etapa">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
          <div className="border-t border-gray-200 pt-2" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {tabs.map(({ id: tid, label, icon: Icon }) => (
          <button key={tid} onClick={() => setActiveTab(tid)}
            className={`flex items-center gap-2 px-3 md:px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tid ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'units' && (
        <div className="space-y-4">
          {isAdmin && (
            <button onClick={() => setCreateOpen(true)}
              className="btn-primary flex items-center gap-2 text-sm">
              <Plus size={16} /> Nueva Unidad de Negocio
            </button>
          )}

          {bus.map(bu => {
            const phases     = bu.phases ?? []
            const progress   = phases.length
              ? Math.round(phases.reduce((s, p) => s + (p.progress ?? 0), 0) / phases.length)
              : 0
            const completed  = phases.filter(p => p.status === 'completed').length
            const inProgress = phases.filter(p => p.status === 'in_progress').length

            return (
              <div key={bu.id} className="w-full card p-5 hover:shadow-md hover:border-brand-300 transition-all group">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate(`/consultancy/${id}/bu/${bu.id}`)}
                    className="flex items-center gap-4 flex-1 text-left min-w-0"
                  >
                    <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center text-xl flex-shrink-0">
                      🏢
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 group-hover:text-brand-700 transition-colors">
                          {bu.name}
                        </span>
                        <StatusBadge status={bu.status} />
                      </div>
                      {bu.description && (
                        <p className="text-xs text-gray-400 mb-2 line-clamp-1">{bu.description}</p>
                      )}
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-400">
                          {completed}/{phases.length} etapas completas
                          {inProgress > 0 && ` · ${inProgress} en progreso`}
                        </span>
                        <div className="flex-1 max-w-xs">
                          <ProgressBar value={progress} size="sm" showLabel={false} />
                        </div>
                        <span className="text-xs text-gray-500">{progress}%</span>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-gray-300 group-hover:text-brand-400 flex-shrink-0 transition-colors" />
                  </button>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0 ml-1">
                      <button onClick={e => openEditBu(e, bu)}
                        className="p-2 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition-colors" title="Editar unidad">
                        <Pencil size={14} />
                      </button>
                      <button onClick={e => deleteBu(e, bu.id)}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar unidad">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {bus.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <Building2 size={40} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium">No hay unidades de negocio</p>
              {isAdmin && (
                <p className="text-sm mt-1">Crea la primera unidad para comenzar la consultoría</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Consolidación Estrategia Corporativa (after BUs) ── */}
      {phasesAfter.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 pt-2">
            <span className="text-base">🏆</span>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Consolidación Corporativa</h2>
          </div>
          {phasesAfter.map(phase => {
            const sections    = phase.sections ?? []
            const completed   = sections.filter(s => s.status === 'completed').length
            const review      = sections.filter(s => s.status === 'review').length
            const inProgress  = sections.filter(s => s.status === 'in_progress').length
            const blocked     = sections.filter(s => s.status === 'blocked').length
            const pending     = sections.filter(s => !s.status || s.status === 'pending').length
            const totalSecs   = sections.length
            const widthOf     = n => totalSecs > 0 ? `${(n / totalSecs) * 100}%` : '0%'
            return (
              <div key={phase.id} className="w-full card p-4 hover:shadow-md hover:border-brand-300 transition-all group border-l-4 border-l-brand-400">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/consultancy/${id}/phase/${phase.id}`)}
                    className="flex items-center gap-3 flex-1 text-left min-w-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-900 group-hover:text-brand-700 transition-colors">
                          {phase.name}
                        </span>
                        <StatusBadge status={phase.status} />
                      </div>
                      <p className="text-xs text-gray-400 mb-2">Sintetiza las estrategias de todas las unidades de negocio</p>
                      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                        {completed  > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{completed} completada{completed !== 1 ? 's' : ''}</span>}
                        {review     > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />{review} en revisión</span>}
                        {inProgress > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />{inProgress} en progreso</span>}
                        {blocked    > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />{blocked} bloqueada{blocked !== 1 ? 's' : ''}</span>}
                        {pending    > 0 && totalSecs > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-gray-400" />{pending} por empezar</span>}
                        {totalSecs === 0 && <span className="text-[11px] text-gray-400 italic">Sin secciones</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-xs h-2 bg-gray-100 rounded-full overflow-hidden flex">
                          <div className="bg-emerald-500 h-full transition-all" style={{ width: widthOf(completed) }} />
                          <div className="bg-violet-500 h-full transition-all"  style={{ width: widthOf(review) }} />
                          <div className="bg-blue-500 h-full transition-all"    style={{ width: widthOf(inProgress) }} />
                          <div className="bg-red-400 h-full transition-all"     style={{ width: widthOf(blocked) }} />
                        </div>
                        <span className="text-xs text-gray-500 tabular-nums">{phase.progress ?? 0}%</span>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-brand-400 flex-shrink-0" />
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                    <AIReportButton consultancyId={id} entityType="consolidation" entityId={phase.id} label="IA" />
                    {isAdmin && (
                      <>
                        <button onClick={e => openEditPhase(e, phase)}
                          className="p-2 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition-colors" title="Editar etapa">
                          <Pencil size={14} />
                        </button>
                        <button onClick={e => deletePhase(e, phase.id)}
                          className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar etapa">
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {activeTab === 'gantt' && (
        <GanttView
          phases={ganttPhases}
          ficha={ficha}
          isAdmin={isAdmin}
          onUpdate={fetchData}
        />
      )}

      {activeTab === 'tareas' && (
        <TaskDashboard tasks={allTasks} />
      )}

      {activeTab === 'files' && (
        <div className="space-y-4">
          <FileUpload entityType="consultancy" entityId={id} onUploaded={() => {}} />
          <FileList entityType="consultancy" entityId={id} />
        </div>
      )}

      {activeTab === 'comments' && (
        <Comments entityType="consultancy" entityId={id} />
      )}

      {activeTab === 'activity' && (
        <ActivityLog consultancyId={id} />
      )}
        </> /* end projectPhase === 'strategic' */
      )}

      {/* ── TACTICAL PLAN ── */}
      {projectPhase === 'tactical' && (
        <TacticalPlanView consultancyId={id} />
      )}

      {/* ── EXECUTION ── */}
      {projectPhase === 'execution' && (
        <ExecutionView consultancyId={id} consultancy={consultancy} />
      )}

      {/* Create BU modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nueva Unidad de Negocio">
        <form onSubmit={createBU} className="space-y-4">
          <div>
            <label className="label">Nombre de la unidad *</label>
            <input className="input" required value={buForm.name}
              onChange={e => setBuForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Bebidas Premium, Canal Retail, Mercado B2B..." />
          </div>
          <div>
            <label className="label">Descripción (opcional)</label>
            <textarea className="input resize-none" rows={3} value={buForm.description}
              onChange={e => setBuForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del alcance de esta unidad..." />
          </div>
          <div className="bg-brand-50 rounded-lg p-3 text-xs text-brand-700">
            <strong>ℹ️ Se crearán automáticamente las 5 etapas metodológicas</strong> de NVC con todas sus
            secciones predefinidas (Entorno, Industria, Mercados, Competitivo y Estrategia).
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setCreateOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? 'Aplicando plantilla...' : 'Crear Unidad'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit BU modal */}
      <Modal open={editBuOpen} onClose={() => setEditBuOpen(false)} title="Editar Unidad de Negocio">
        <form onSubmit={saveEditBu} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={editBuForm.name}
              onChange={e => setEditBuForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={editBuForm.description}
              onChange={e => setEditBuForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditBuOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingBu} className="btn-primary flex-1">
              {savingBu ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Consultancy modal */}
      <Modal open={editConsOpen} onClose={() => setEditConsOpen(false)} title="Editar Consultoría">
        <form onSubmit={saveEditCons} className="space-y-4">
          <div>
            <label className="label">Nombre del proyecto *</label>
            <input className="input" required value={editConsForm.name}
              onChange={e => setEditConsForm(f => ({...f, name: e.target.value}))} />
          </div>
          <div>
            <label className="label">Empresa cliente *</label>
            <input className="input" required value={editConsForm.client_company}
              onChange={e => setEditConsForm(f => ({...f, client_company: e.target.value}))} />
          </div>
          <div>
            <label className="label">Industria</label>
            <input className="input" value={editConsForm.industry}
              onChange={e => setEditConsForm(f => ({...f, industry: e.target.value}))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={editConsForm.description}
              onChange={e => setEditConsForm(f => ({...f, description: e.target.value}))} />
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={editConsForm.status}
              onChange={e => setEditConsForm(f => ({...f, status: e.target.value}))}>
              <option value="active">Activa</option>
              <option value="paused">Pausada</option>
              <option value="completed">Completada</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditConsOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingCons} className="btn-primary flex-1">
              {savingCons ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Phase modal */}
      <Modal open={editPhaseOpen} onClose={() => setEditPhaseOpen(false)} title="Editar Etapa">
        <form onSubmit={saveEditPhase} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={editPhaseForm.name}
              onChange={e => setEditPhaseForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={editPhaseForm.description}
              onChange={e => setEditPhaseForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditPhaseOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingPhase} className="btn-primary flex-1">
              {savingPhase ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// ─── Gantt View ───────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  pending:     '#e2e8f0',
  in_progress: '#93c5fd',
  review:      '#c4b5fd',
  completed:   '#6ee7b7',
  blocked:     '#fca5a5',
}
const STATUS_LABELS = {
  pending: 'Pendiente', in_progress: 'En progreso',
  review: 'En revisión', completed: 'Completado', blocked: 'Bloqueado',
}
const STATUS_DOT = {
  pending: 'bg-gray-300', in_progress: 'bg-blue-400',
  review: 'bg-violet-400', completed: 'bg-emerald-400', blocked: 'bg-red-400',
}
const MILESTONE_CONFIG = [
  { key: 'fecha_inicio',        label: 'Inicio',        color: '#0ea5e9' },
  { key: 'fecha_diagnostico',   label: 'Diagnóstico',   color: '#f59e0b' },
  { key: 'fecha_entrega_plan',  label: 'Entrega plan',  color: '#8b5cf6' },
  { key: 'fecha_cierre',        label: 'Cierre',        color: '#10b981' },
]

function GanttView({ phases, ficha, isAdmin, onUpdate }) {
  const [editingId, setEditingId] = useState(null)
  const [form, setForm]           = useState({ fecha_inicio: '', fecha_fin: '' })
  const [saving, setSaving]       = useState(false)

  // Compute overall timeline bounds
  const allDateStrings = [
    ...(phases.flatMap(p => [p.fecha_inicio, p.fecha_fin])),
    ...(MILESTONE_CONFIG.map(m => ficha?.[m.key])),
  ].filter(Boolean).sort()

  const hasAnyDate = allDateStrings.length > 0
  const refStart   = hasAnyDate ? new Date(allDateStrings[0] + 'T00:00:00')  : new Date()
  const refEnd     = hasAnyDate ? new Date(allDateStrings[allDateStrings.length - 1] + 'T00:00:00') : new Date(+new Date() + 90 * 86400000)

  // Add a little padding on each side
  const chartStart = new Date(+refStart - 5 * 86400000)
  const chartEnd   = new Date(+refEnd   + 5 * 86400000)
  const totalDays  = Math.ceil((chartEnd - chartStart) / 86400000)

  const toDay = d => d ? Math.round((new Date(d + 'T00:00:00') - chartStart) / 86400000) : null

  // Chart data — one row per phase
  const chartData = phases.map(p => {
    const start = toDay(p.fecha_inicio)
    const end   = toDay(p.fecha_fin)
    const dur   = (start !== null && end !== null) ? Math.max(1, end - start) : 0
    const shortName = p.name.length > 28 ? p.name.slice(0, 26) + '…' : p.name
    return {
      id: p.id, name: shortName, fullName: p.name,
      status: p.status ?? 'pending',
      offset: start ?? 0, duration: dur,
      hasDate: start !== null && end !== null,
      startDate: p.fecha_inicio, endDate: p.fecha_fin,
    }
  })

  // Month tick marks along X axis
  const monthTicks = (() => {
    const ticks = []
    const d = new Date(chartStart)
    d.setDate(1)
    while (d <= chartEnd) {
      ticks.push({
        day: Math.round((d - chartStart) / 86400000),
        label: d.toLocaleDateString('es', { month: 'short', year: '2-digit' }),
      })
      d.setMonth(d.getMonth() + 1)
    }
    return ticks
  })()

  // Milestone lines
  const milestones = MILESTONE_CONFIG
    .filter(m => ficha?.[m.key])
    .map(m => ({ ...m, day: toDay(ficha[m.key]) }))

  function openEdit(phase) {
    setEditingId(phase.id)
    setForm({ fecha_inicio: phase.fecha_inicio ?? '', fecha_fin: phase.fecha_fin ?? '' })
  }

  async function saveDates(e) {
    e.preventDefault()
    setSaving(true)
    await supabase.from('phases').update({
      fecha_inicio: form.fecha_inicio || null,
      fecha_fin:    form.fecha_fin    || null,
    }).eq('id', editingId)
    setEditingId(null)
    setSaving(false)
    onUpdate()
  }

  const ROW_H   = 44
  const chartH  = phases.length * ROW_H + 50

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const d = payload.find(p => p.dataKey === 'duration')?.payload
    if (!d?.hasDate) return null
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm min-w-[180px]">
        <p className="font-semibold text-gray-800 mb-2 leading-tight">{d.fullName}</p>
        <div className="space-y-0.5 text-xs text-gray-500">
          <p>🗓 Inicio: <span className="text-gray-700 font-medium">{d.startDate}</span></p>
          <p>🏁 Fin: <span className="text-gray-700 font-medium">{d.endDate}</span></p>
          <p>⏱ Duración: <span className="text-gray-700 font-medium">{d.duration} días</span></p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-800 text-base">Cronograma del Proyecto</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {hasAnyDate
              ? `${allDateStrings[0]} → ${allDateStrings[allDateStrings.length - 1]}`
              : 'Sin fechas definidas — usa el lápiz para asignarlas'}
          </p>
        </div>
        {/* Milestone legend */}
        {milestones.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            {milestones.map(m => (
              <div key={m.key} className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                {m.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status legend */}
      <div className="flex items-center gap-3 flex-wrap">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
            <div className="w-3 h-3 rounded-sm" style={{ background: STATUS_COLORS[key] }} />
            {label}
          </div>
        ))}
      </div>

      {/* Gantt Chart */}
      <div className="card overflow-hidden p-4">
        {hasAnyDate ? (
          <ResponsiveContainer width="100%" height={chartH}>
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 8, right: 16, left: 8, bottom: 24 }}
              barSize={20}
            >
              <XAxis
                type="number"
                domain={[0, totalDays]}
                ticks={monthTicks.map(m => m.day)}
                tickFormatter={val => monthTicks.find(m => m.day === val)?.label ?? ''}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={170}
                tick={{ fontSize: 12, fill: '#374151' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />

              {/* Month grid lines */}
              {monthTicks.map(m => (
                <ReferenceLine key={m.day} x={m.day} stroke="#f1f5f9" strokeWidth={1} />
              ))}

              {/* Milestone markers */}
              {milestones.map(m => (
                <ReferenceLine
                  key={m.key}
                  x={m.day}
                  stroke={m.color}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  label={{ value: m.label, position: 'top', fontSize: 10, fill: m.color }}
                />
              ))}

              {/* Transparent offset bar (creates left padding for each row) */}
              <Bar dataKey="offset" stackId="g" fill="transparent" isAnimationActive={false} />

              {/* Actual duration bar */}
              <Bar
                dataKey="duration"
                stackId="g"
                radius={[4, 4, 4, 4]}
                isAnimationActive={false}
                onClick={isAdmin ? d => openEdit(d) : undefined}
                style={isAdmin ? { cursor: 'pointer' } : {}}
              >
                {chartData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.hasDate ? STATUS_COLORS[entry.status] : '#f1f5f9'}
                    stroke={entry.hasDate ? 'none' : '#e2e8f0'}
                    strokeDasharray={entry.hasDate ? undefined : '4 2'}
                    opacity={entry.hasDate ? 1 : 0.6}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-16 text-gray-400">
            <Calendar size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">Sin fechas definidas</p>
            <p className="text-xs mt-1">Asigna fechas a las etapas desde la lista de abajo</p>
          </div>
        )}
      </div>

      {/* Phase list with date editors */}
      <div className="card overflow-hidden p-0">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Etapas del proyecto</p>
        </div>
        <div className="divide-y divide-gray-100">
          {phases.map(phase => (
            <div key={phase.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors gap-4">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[phase.status ?? 'pending']}`} />
                <span className="text-sm text-gray-700 truncate">{phase.name}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {phase.fecha_inicio ? (
                  <span className="text-xs text-gray-500 tabular-nums">
                    {new Date(phase.fecha_inicio + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                    {' → '}
                    {phase.fecha_fin
                      ? new Date(phase.fecha_fin + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })
                      : <span className="text-gray-300">sin fin</span>}
                    {phase.fecha_inicio && phase.fecha_fin && (
                      <span className="ml-1.5 text-gray-400">
                        ({Math.round((new Date(phase.fecha_fin) - new Date(phase.fecha_inicio)) / 86400000)}d)
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-gray-300 italic">Sin fecha</span>
                )}
                {isAdmin && (
                  <button
                    onClick={() => openEdit(phase)}
                    className="p-1.5 text-gray-300 hover:text-brand-500 hover:bg-brand-50 rounded-lg transition-colors"
                    title="Editar fechas"
                  >
                    <Pencil size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit dates modal */}
      <Modal open={!!editingId} onClose={() => setEditingId(null)} title="Fechas de la etapa">
        <form onSubmit={saveDates} className="space-y-4">
          <div>
            <label className="label">Fecha de inicio</label>
            <input type="date" className="input"
              value={form.fecha_inicio}
              onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} />
          </div>
          <div>
            <label className="label">Fecha de fin</label>
            <input type="date" className="input"
              value={form.fecha_fin}
              onChange={e => setForm(f => ({ ...f, fecha_fin: e.target.value }))} />
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500">
            💡 Los hitos globales (Inicio, Diagnóstico, Entrega, Cierre) se configuran en la <strong>Ficha del Proyecto</strong>.
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setEditingId(null)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors disabled:opacity-50">
              {saving ? 'Guardando…' : 'Guardar fechas'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
